// Left-hand sidebar mounted inside the main Content flex row so opening
// it shrinks the Scene's width rather than overlaying.
//
// Layout:
//   [CategoryNavbar]  — icon-only row, mirrors ResponsiveSwitching
//   [active list]     — Pages | Components | DesignSystemPages
//
// State contract:
//   root.sidebarOpen       → slide in/out (via `hide:`)
//   root.explorerContent   → which category list is shown
//   root.activePage        → currently-previewed page route
//   root.activeComponent   → currently-previewed component key
//   root.dsPage            → currently-picked ds sub-page

export const DS_PAGES = [
  { key: 'colors', icon: 'color', title: 'Colors', dsPage: '/colors' },
  { key: 'typography', icon: 'typography', title: 'Typography', dsPage: '/typography' },
  { key: 'spacing', icon: 'space', title: 'Spacing', dsPage: '/spacing' },
  { key: 'icons', icon: 'star', title: 'Icons', dsPage: '/icons' },
  { key: 'transitions', icon: 'play', title: 'Transitions', dsPage: '/transitions' },
  { key: 'layout', icon: 'grid', title: 'Layout', dsPage: '/layout' },
  { key: 'misc', icon: 'settings', title: 'Misc', dsPage: '/misc' }
]

// Mirrors the ResponsiveSwitching shape: each entry carries its own
// sprite id. Icons with an `Outline` variant toggle filled↔outline on
// active like ResponsiveSwitching does; the rest fall back to a theme
// swap.
// Icons paired with explicit `Outline` variants so the active-state
// sprite swap has somewhere to go (pages↔pagesOutline,
// layers↔layersOutline, color↔colorOutline).
const CATEGORIES = [
  { key: 'pages', icon: 'pages', title: 'Pages' },
  { key: 'components', icon: 'layers', title: 'Components' },
  { key: 'designSystem', icon: 'color', title: 'Design System' }
]

const navigateIframeTo = (el, route) => {
  const doc = el.context?.document || document
  const iframe = doc.getElementById('preview-app-iframe')
  const iwin = iframe?.contentWindow
  if (!iwin) return
  try {
    iwin.history.pushState(null, '', route)
    iwin.dispatchEvent(new iwin.PopStateEvent('popstate'))
  } catch (e) {
    console.warn('[PreviewSidebar] iframe navigation failed:', e)
  }
}

const ensureDsEmbedded = async (el, initialState) => {
  const doc = el.context?.document || document
  const rootEl = el.getRoot()
  const data = rootEl?.state?.data
  if (!data) return null
  let iframe = null
  for (let i = 0; i < 20; i++) {
    iframe = doc.getElementById('preview-ds-iframe')
    if (iframe) break
    await new Promise(r => setTimeout(r, 50))
  }
  if (!iframe) return null
  const { embedDesignSystem } = await import(
    '../../shared/iframeApp/index.js'
  )
  await embedDesignSystem(iframe, data, initialState)
  return iframe
}

const previewComponent = async (el, key) => {
  const doc = el.context?.document || document
  const rootEl = el.getRoot()
  if (!rootEl?.state?.data) return
  if (rootEl.state.viewMode === 'designSystem') {
    rootEl.state.update({ viewMode: 'app' })
    if (rootEl.Scene) rootEl.Scene.update()
  }
  const iframe = doc.getElementById('preview-app-iframe')
  if (!iframe) return
  const { embedComponent } = await import(
    '../../shared/iframeApp/index.js'
  )
  await embedComponent(iframe, rootEl.state.data, { key })
}

// Swap Icon sprite href between filled and outline on sibling children,
// matching ResponsiveSwitching. Skips children whose `icon` has no
// `iconOutline` variant (e.g. `sfComponents`).
const swapSiblingIcons = (parent, activeKey) => {
  for (const childKey of parent.__ref?.__children || []) {
    const child = parent[childKey]
    if (!child?.Icon?.node) continue
    const base = child.state?.icon
    if (!base) continue
    const outlineId = base + 'Outline'
    const ownerDoc = child.Icon.node.ownerDocument
    const hasOutline = !!ownerDoc.getElementById(outlineId)
    const isActive = child.state?.key === activeKey
    const spriteId = isActive ? base : hasOutline ? outlineId : base
    const useEl = child.Icon.node.querySelector('use')
    if (useEl) useEl.setAttribute('xlink:href', '#' + spriteId)
  }
}

// Pretty list-row. Active-state done via direct DOM class toggle in
// onClick (rather than reactive factories) because DOMQL v3.14's signal
// system doesn't track mutations to parent-scope plain objects, so
// sibling re-renders don't see updated `scope.activeKey` values. Same
// visual result, without the dependency-tracking fragility.
const ACTIVE_BG = 'rgba(255,255,255,0.08)'
const HOVER_BG = 'rgba(255,255,255,0.04)'

const applyActiveStyles = (node, active) => {
  if (!node) return
  node.style.background = active ? ACTIVE_BG : ''
  node.style.color = active ? 'var(--color-title)' : 'var(--color-caption)'
}

const listRow = ({ keyOf, iconFn, textFn, onClick }) => ({
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center',
  gap: 'Y',
  padding: 'Y Z',
  round: 'Z',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: 'Z',
  background: 'transparent',
  color: 'caption',
  transition: 'B defaultBezier',
  transitionProperty: 'background, color',
  ':hover': {
    background: HOVER_BG,
    color: 'title'
  },
  // First paint: if the parent list already has a seeded active key,
  // apply the active styling directly to the matching node.
  onRender: (el, s) => {
    const activeKey = el.parent?.scope?.activeKey
    if (activeKey != null && activeKey === keyOf(s)) {
      applyActiveStyles(el.node, true)
    }
  },
  Icon: iconFn
    ? {
        extends: 'Icon',
        icon: iconFn,
        boxSize: 'Z2',
        opacity: '0.75'
      }
    : null,
  Label: {
    tag: 'span',
    flex: '1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: textFn
  },
  onClick (ev, el, s) {
    const parent = el.parent
    const myKey = keyOf(s)
    // Sweep siblings back to default, light up the clicked node.
    for (const k of parent?.__ref?.__children || []) {
      const child = parent[k]
      if (!child?.node) continue
      applyActiveStyles(child.node, child === el)
    }
    if (parent?.scope) parent.scope.activeKey = myKey
    return onClick?.(ev, el, s)
  }
})

export const PreviewSidebar = {
  extends: 'AppSidebar',
  hide: (el, s) => !s.root?.sidebarOpen,
  width: 'F2',
  minWidth: 'F2',
  fontSize: 'Z',
  padding: '0',
  gap: '0',

  Content: {
    flex: '1',
    overflow: 'auto',
    flow: 'column',
    gap: '0',
    padding: '0',

    // Category navbar — mirrors ResponsiveSwitching exactly:
    //   extends: 'NavbarButtonSet' → children become NavbarButtons
    //   reactive `icon` prop swaps filled↔outline on active
    //   manual sprite swap on click so the `use href=…` updates
    CategoryNavbar: {
      extends: 'NavbarButtonSet',
      color: 'dim',
      fontSize: 'Z1',
      align: 'center',
      padding: 'Y Z',
      gap: 'W2',
      width: '100%',
      borderBottom: '1px solid',
      borderBottomColor: 'line',
      childrenAs: 'state',
      children: CATEGORIES,
      childProps: {
        isActive: (el, s) => s.key === s.root?.explorerContent,
        icon: (el, s) => {
          const active = s.key === s.root?.explorerContent
          return active ? s.icon : s.icon + ' outline'
        },
        title: (el, s) => s.title,
        '.isActive': { theme: 'navbarButton-active' },
        '!isActive': { theme: 'navbarButton' },
        onClick: async (ev, el, s) => {
          const rs = el.getRootState()
          const next = { explorerContent: s.key }
          if (s.key === 'designSystem') next.viewMode = 'designSystem'
          else if (rs.viewMode === 'designSystem') next.viewMode = 'app'
          rs.update(next)
          const rootEl = el.getRoot()
          if (rootEl?.Scene) rootEl.Scene.update()
          const sidebar = rootEl?.PreviewSidebar
          const listEl = sidebar?.Content?.List
          // Category switch — seed the list's active-key from whichever
          // root field applies so the relevant row is highlighted.
          if (listEl?.scope) {
            if (s.key === 'designSystem') listEl.scope.activeKey = rs.dsPage
            else if (s.key === 'components') listEl.scope.activeKey = rs.activeComponent
            else listEl.scope.activeKey = rs.activePage
          }
          // Force the list wrapper to re-run its `children:` factory and
          // every category button to re-evaluate its reactive props.
          listEl?.update?.()
          for (const k of el.parent?.__ref?.__children || []) {
            el.parent[k]?.update?.()
          }
          swapSiblingIcons(el.parent, s.key)
          if (s.key === 'designSystem') {
            await ensureDsEmbedded(el, {
              dsPage: rs.dsPage,
              dsNav: rs.dsNav
            })
          }
        }
      }
    },

    // Single reactive list — swaps its children based on which
    // category is active. Avoids the DOMQL-v3.14 gotcha where multiple
    // sibling children with `if:` don't all re-evaluate on root-state
    // changes; one `children:` factory always re-runs.
    List: {
      flow: 'column',
      gap: 'V',
      padding: 'Y Y2',
      flex: '1',
      overflow: 'auto',
      // `activeKey` is mutated inside list-item onClicks (same pattern
      // ResponsiveSwitching uses with `scope.previewSize`). Scope gives
      // us a stable per-list signal children can read locally.
      scope: { activeKey: null },
      onRender: (el, s) => {
        // Seed `activeKey` from root state so a reload remembers the
        // last picked item in each category.
        const cat = s.root?.explorerContent || 'pages'
        if (!el.scope) el.scope = { activeKey: null }
        if (el.scope.activeKey != null) return
        if (cat === 'designSystem') el.scope.activeKey = s.root?.dsPage
        else if (cat === 'components') el.scope.activeKey = s.root?.activeComponent
        else el.scope.activeKey = s.root?.activePage
      },
      childrenAs: 'state',
      children: (el, s) => {
        const cat = s.root?.explorerContent || 'pages'
        if (cat === 'designSystem') {
          return DS_PAGES.map((p) => ({ ...p, _kind: 'ds' }))
        }
        if (cat === 'components') {
          const comps = s.root?.data?.components || {}
          return Object.keys(comps).map((key) => ({ key, _kind: 'comp' }))
        }
        const pages = s.root?.data?.pages || {}
        return Object.keys(pages).map((route) => ({
          key: route,
          route,
          _kind: 'page'
        }))
      },
      childProps: listRow({
        // `keyOf` picks the identity each list item uses against
        // `scope.activeKey`. Using `_kind` to branch avoids the
        // state-proxy falling through to root's `title` when the row
        // doesn't own a matching field.
        keyOf: (s) => {
          if (s._kind === 'ds') return s.dsPage
          if (s._kind === 'comp') return s.key
          return s.route
        },
        iconFn: (el, s) => {
          if (s._kind === 'ds') return s.icon
          if (s._kind === 'comp') return 'layers'
          return 'document'
        },
        textFn: (el, s) => {
          if (s._kind === 'ds') return s.title
          if (s._kind === 'comp') return s.key
          return s.route
        },
        onClick: async (ev, el, s) => {
          if (s._kind === 'ds') {
            const rs = el.getRootState()
            const nextDsNav = (rs.dsNav || 0) + 1
            rs.update({
              dsPage: s.dsPage,
              dsNav: nextDsNav,
              viewMode: 'designSystem'
            })
            const doc = el.context?.document || document
            const dsIframe = doc.getElementById('preview-ds-iframe')
            const innerRs = dsIframe?.__dsRootState
            if (innerRs?.update) {
              innerRs.update({ dsPage: s.dsPage, dsNav: nextDsNav })
            } else {
              await ensureDsEmbedded(el, {
                dsPage: s.dsPage,
                dsNav: nextDsNav
              })
            }
          } else if (s._kind === 'comp') {
            el.getRootState().update({ activeComponent: s.key })
            await previewComponent(el, s.key)
          } else {
            el.getRootState().update({ activePage: s.route })
            navigateIframeTo(el, s.route)
          }
          // DOMQL v3.14 doesn't always re-evaluate reactive `background` /
          // `color` factories on nested children when root-state keys
          // they read change. Same workaround ThemeSwitcher /
          // ResponsiveSwitching use: manually call `update()` on every
          // sibling so the active-state styling refreshes.
          const parent = el.parent
          for (const k of parent?.__ref?.__children || []) {
            parent[k]?.update?.()
          }
        }
      })
    }
  }
}
